import React, { CSSProperties, useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { totallyDarkeChartsTheme } from '../../util/theme';
import { DataZoomEvent } from '../../util/types';
import { scale } from '../../util/numbers';

// let cfdChart: echarts.EChartsType;

const DEFAULT_MAX_PERCENTILE = .95;
const useDarkMode = (localStorage.getItem('darkMode') === 'true');

interface CfdChartProps {
  style: CSSProperties;
  travelTimeSeriesList?: TravelTimeSeries[];
  maxTravelTimeAllData: number;
  title?: string;
  onDelete?: (index: number) => void;
  index?: number;
}

export interface TravelTimeSeries {
  data: number[][];

  [key: string]: unknown;

  type: string;
}


const getTravelTimeForPercentile = (travelTimes: number[][], percentile: number) => {
  return travelTimes[Math.floor(travelTimes.length * percentile)][0];
};

const guidGenerator = () => {
  const S4 = () => {
    // tslint:disable-next-line:no-bitwise
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4());
};

const CfdChart = (props: CfdChartProps) => {
  const id = guidGenerator();
  const cfdChart = useRef<echarts.EChartsType | null>(null);

  const getOption = (travelTimesSeries: TravelTimeSeries[], title?: string) => ({
    grid: [{
      top: 55,
      bottom: 100,
      right: 10,
      left: 50,
    }],
    title: {
      text: title,
      top: 0,
    },
    legend: {
      show: true,
      top: 25,
      type: 'scroll',
      // width: '90%',
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'cross',
        animation: false,
        label: {
          formatter: (d: { value: number, axisDimension: string }) => {
            return `${d.value.toFixed(1)}${d.axisDimension === 'y' ? '%' : ''}`;
          },
          backgroundColor: useDarkMode ? 'rgba(227,227,227,0.8)' : 'rgba(28,28,28,0.8)',
          borderColor: '#aaa',
          borderWidth: 1,
          shadowBlur: 0,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          color: (useDarkMode) ? '#000' : '#fff',
        },
      },
    },
    axisPointer: {
      show: true,
      lineStyle: {
        // color: '#000',
        color: (useDarkMode) ? '#fff' : '#000',
        width: 1,
        type: 'dashed',
      },
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: { show: true },
        ...(props.onDelete &&
          {
            myDeleteTool: {
              title: 'Delete Slice',
              show: true,
              icon: 'path://M 357 378C 344 378 332 390 333 403C 333 403 329 848 329 848C 329 857 333 866 341 870C 349 875 359 875 366 870C 374 866 379 858 379 849C 379 849 383 404 383 404C 383 397 380 391 375 386C 371 381 364 378 357 378C 357 378 357 378 357 378M 650 375C 636 375 625 386 625 400C 625 400 625 850 625 850C 625 859 630 867 637 872C 645 876 655 876 663 872C 670 867 675 859 675 850C 675 850 675 400 675 400C 675 393 672 387 668 382C 663 377 656 375 650 375C 650 375 650 375 650 375M 500 375C 486 375 475 386 475 400C 475 400 475 850 475 850C 475 859 480 867 487 872C 495 876 505 876 513 872C 520 867 525 859 525 850C 525 850 525 400 525 400C 525 393 522 387 518 382C 513 377 506 375 500 375C 500 375 500 375 500 375M 198 299C 198 299 800 299 800 299C 800 299 800 850 800 850C 800 913 759 950 700 950C 700 950 300 950 300 950C 238 950 200 911 201 855C 201 855 198 299 198 299M 438 138C 438 138 438 187 438 187C 438 187 563 187 563 187C 563 187 563 138 563 138C 563 138 438 138 438 138M 425 63C 425 63 575 63 575 63C 609 63 638 91 638 125C 638 125 638 187 638 187C 638 187 849 187 849 187C 870 187 887 204 887 225C 887 245 870 262 849 262C 849 262 151 263 151 263C 130 263 113 246 113 225C 113 205 130 188 151 188C 151 188 363 188 363 188C 363 188 363 125 363 125C 363 125 362 125 362 125C 362 91 391 63 425 63C 425 63 425 63 425 63',
              onclick: () => {
                if (props.onDelete && (props.index !== undefined && props.index !== null)) {
                  props.onDelete(props.index as number);
                }
              },
            },
          }
        ),
      },
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: Math.ceil(props.maxTravelTimeAllData / getRoundToNearest()) * getRoundToNearest(),
      name: 'Travel Time (minutes)',
      nameLocation: 'center',
      nameGap: 25,
      axisLabel: { show: true },
      axisTick: { show: true },
    },
    yAxis: {
      // axisLabel: {show: false,},
      axisTick: { show: false },
      minInterval: 25,
      maxInterval: 25,
      max: 100,
      type: 'value',
      name: 'Cumulative Frequency',
      nameLocation: 'center',
      nameGap: 30,
    },
    dataZoom: {
      xAxisIndex: 0,
      type: 'slider',
      filterMode: 'none',
      showDataShadow: false,
      endValue: Math.ceil(
        (Math.max(
          ...travelTimesSeries.map(travelTimes => (
            (travelTimes.data && travelTimes.data.length > 1)
              ? getTravelTimeForPercentile(travelTimes.data, DEFAULT_MAX_PERCENTILE) : 0)),
        ) || props.maxTravelTimeAllData)
        / getRoundToNearest()) * getRoundToNearest(),
    },
    series: travelTimesSeries,
  });

  useEffect(() => {
    if (props.travelTimeSeriesList && props.travelTimeSeriesList.length) {
      const opt = getOption(props.travelTimeSeriesList.filter(s => s.data.length), props.title);

      if (!cfdChart.current) {
        echarts.registerTheme('totally-dark-theme', totallyDarkeChartsTheme);
        cfdChart.current = echarts.init(document.getElementById(id) as HTMLElement, (useDarkMode) ? 'totally-dark-theme' : 'light', {
          renderer: 'canvas',
          locale: 'EN',
        });
        cfdChart.current.on('resize', () => {
          cfdChart.current?.resize();
        });
        cfdChart.current.on('datazoom', dataZoomListener);
        cfdChart.current?.setOption(opt, { notMerge: true });
      }
      cfdChart.current?.setOption({ series: opt.series });
    }
  }, [props.travelTimeSeriesList]);

  const getRoundToNearest = (): number => {
    // e.g. if 5, round the start, end to the nearest 5 minutes.
    if (props.maxTravelTimeAllData < 10) return 0.5;
    else if (props.maxTravelTimeAllData >= 10 && props.maxTravelTimeAllData < 60) return 1;
    else return 5;
  };

  const dataZoomListener = (e: unknown) => {
    const event = e as DataZoomEvent;
    if (Object.keys(event).includes('startValue')) return;
    const { start, end } = event;

    const min = scale(start, 0, 100, 0, props.maxTravelTimeAllData);
    const max = scale(end, 0, 100, 0, props.maxTravelTimeAllData);

    const roundToNearest = getRoundToNearest();

    // Already correct, no need to adjust.
    if (min % roundToNearest === 0 && max % roundToNearest === 0) return;

    const startRounded = Math.floor((min) / roundToNearest) * roundToNearest;
    const endRounded = Math.ceil((max) / roundToNearest) * roundToNearest;

    if (cfdChart.current) cfdChart.current.dispatchAction({
      type: 'dataZoom',
      dataZoomIndex: 0,
      // start: 45,
      // end: 55
      startValue: startRounded,
      endValue: endRounded,
    });
  };

  return (
    <>
      <div style={props.style} id={id}/>
    </>
  );
};


export default CfdChart;
